import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

/** Max number of product grid DOM trees to keep in memory. */
const MAX_CACHE_SIZE = 10;

/** Max concurrent prefetch requests to avoid saturating the connection pool. */
const MAX_CONCURRENT_PREFETCHES = 2;

/**
 * Module-level cache shared across all quick-add code paths.
 * Keyed by normalized URL (variant param stripped) so different variant
 * selections for the same product share one cache entry.
 * Evicts the oldest entry (FIFO) when the cache exceeds MAX_CACHE_SIZE.
 * @type {Map<string, Element>}
 */
const contentCache = new Map();

/** @type {Map<string, Promise<Element|null>>} */
const pendingPrefetches = new Map();

/**
 * Normalizes a product page URL to a consistent cache key.
 * Strips the variant param since variant selection is synced post-load.
 * @param {string} url
 * @returns {string}
 */
function getCacheKey(url) {
  try {
    const u = new URL(url);
    u.searchParams.delete('variant');
    return u.toString();
  } catch {
    return url;
  }
}

/**
 * Gets cached product grid content for a URL, or null.
 * @param {string} url
 * @returns {Element | null}
 */
export function getCachedContent(url) {
  return contentCache.get(getCacheKey(url)) ?? null;
}

/**
 * Stores product grid content in the shared cache.
 * @param {string} url
 * @param {Element} element
 */
export function cacheProductContent(url, element) {
  const key = getCacheKey(url);

  if (contentCache.size >= MAX_CACHE_SIZE && !contentCache.has(key)) {
    const oldest = contentCache.keys().next().value;
    contentCache.delete(oldest);
  }

  contentCache.set(key, element);
}

/**
 * Returns a pending prefetch promise for a URL, or null.
 * @param {string} url
 * @returns {Promise<Element|null> | null}
 */
export function getPendingPrefetch(url) {
  return pendingPrefetches.get(getCacheKey(url)) ?? null;
}

/**
 * Starts a background prefetch for a product page URL.
 * Results are stored in the shared contentCache for any code path to use.
 * @param {string} url
 */
export function prefetchProductContent(url) {
  const key = getCacheKey(url);
  if (!url || contentCache.has(key) || pendingPrefetches.has(key)) return;
  if (pendingPrefetches.size >= MAX_CONCURRENT_PREFETCHES) return;

  const promise = (async () => {
    try {
      const res = await fetch(url);
      if (!res.ok) return null;

      const text = await res.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const grid = html.querySelector('[data-product-grid-content]');
      if (!grid) return null;

      const element = /** @type {Element} */ (grid.cloneNode(true));
      cacheProductContent(url, element);
      return element;
    } catch {
      return null;
    } finally {
      pendingPrefetches.delete(key);
    }
  })();

  pendingPrefetches.set(key, promise);
}

/**
 * Retrieves product grid content for a URL, using the shared cache,
 * a pending prefetch, or a fresh fetch as needed. Caches the result.
 * @param {string} url
 * @returns {Promise<Element | null>}
 */
export async function getOrFetchProductContent(url) {
  const pending = getPendingPrefetch(url);
  if (pending) return pending;

  try {
    const res = await fetch(url);
    if (!res.ok) return null;

    const text = await res.text();
    const html = new DOMParser().parseFromString(text, 'text/html');
    const grid = html.querySelector('[data-product-grid-content]');
    if (!grid) return null;

    const element = /** @type {Element} */ (grid.cloneNode(true));
    cacheProductContent(url, element);
    return element;
  } catch {
    return null;
  }
}

/**
 * Toggles the skeleton loading state on the quick-add modal dialog.
 * @param {boolean} show - Whether to show the skeleton
 */
export function showModalSkeleton(show) {
  const dialog = document.querySelector('#sd-quick-add-dialog dialog');
  if (!dialog) return;

  if (show) {
    dialog.setAttribute('data-loading', '');
  } else {
    dialog.removeAttribute('data-loading');
  }
}

/**
 * SD Quick Add Component - Uses SD Product Information in quickview modal
 */
export class SDQuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    // Hosts outside a product-card (e.g. the cart drawer's You May Also Like
    // carousel) have no link to read, so fall back to the url passed in on the
    // element. It is root-relative, hence the base arg.
    const href = productLink?.href || this.dataset.productUrl;

    if (!href) return '';

    const url = new URL(href, window.location.origin);

    // Use the SD mini PDP template (DO-NOT-DELETE suffix prevents merchants from assigning it to products)
    url.searchParams.set('view', 'sd-mini-pdp-DO-NOT-DELETE');

    if (!url.searchParams.has('variant')) {
      const selectedVariantId = this.#getSelectedVariantId();
      if (selectedVariantId) {
        url.searchParams.set('variant', selectedVariantId);
      }
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);

    // Prefetch on hover for the "choose options" quick-add button
    const chooseButton = this.querySelector('.sd-quick-add__button--choose');
    chooseButton?.addEventListener('pointerenter', this.prefetch, { once: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();

    const chooseButton = this.querySelector('.sd-quick-add__button--choose');
    chooseButton?.removeEventListener('pointerenter', this.prefetch);
  }

  /**
   * Prefetches the product page content and caches it for instant modal opening.
   * Delegates to the shared module-level prefetch utility.
   */
  prefetch = () => {
    prefetchProductContent(this.productPageUrl);
  };

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    let productGrid = getCachedContent(currentUrl);

    if (productGrid) {
      await this.updateQuickAddModal(/** @type {Element} */ (productGrid.cloneNode(true)));
      this.#openQuickAddModal();
      return;
    }

    // Not cached — show skeleton and open modal immediately
    showModalSkeleton(true);
    this.#openQuickAddModal();

    productGrid = await getOrFetchProductContent(currentUrl);
    if (productGrid) {
      await this.updateQuickAddModal(/** @type {Element} */ (productGrid.cloneNode(true)));
    }

    showModalSkeleton(false);
  };

  /** @param {SDQuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('sd-quick-add-dialog');
    if (!(dialogComponent instanceof SDQuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('sd-quick-add-dialog');
    if (!(dialogComponent instanceof SDQuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('sd-quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    this.#reinitializeGallery(modalContent);
    this.#reinitializeTabs(modalContent);

    // Notify other modules that modal content has been loaded/updated.
    // Used by sd-shipping-restriction.js to apply state-based restrictions.
    document.dispatchEvent(new CustomEvent('sd:quick-add-content-loaded'));
  }

  /**
   * Reinitializes the SD media gallery after morphing
   * @param {Element} modalContent - The modal content element
   */
  #reinitializeGallery(modalContent) {
    const gallery = modalContent.querySelector('sd-media-gallery');
    if (gallery && typeof gallery.connectedCallback === 'function') {
      gallery.connectedCallback();
    }
  }

  /**
   * Reinitializes the SD tabs after morphing
   * @param {Element} modalContent - The modal content element
   */
  #reinitializeTabs(modalContent) {
    const tabs = modalContent.querySelector('sd-tabs');
    if (tabs && typeof tabs.updatedCallback === 'function') {
      tabs.updatedCallback();
    }
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('sd-quick-add-component')) {
  customElements.define('sd-quick-add-component', SDQuickAddComponent);
}

class SDQuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);

    // The modal's DOM is built once at open for the breakpoint it opened at
    // (updateQuickAddModal rebuilds the layout for mobile), so crossing 750px
    // with it open strands the other breakpoint's DOM under the new CSS —
    // stacked desktop markup, no sticky buy bar. SDQuickAddComponent already
    // closes on cross for the product-card path, but the cart drawer's
    // recommendations open this dialog through sd-cta-quick-view, which has no
    // card component on the page. Owning the close here covers every opener.
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sd-quick-add] Event promise rejected:', error);
      });
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleBreakpointChange = () => {
    this.closeDialog();
  };

  #handleDialogClose = () => {
    // Reset skeleton loading state when dialog closes
    this.refs.dialog?.removeAttribute('data-loading');

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('sd-quick-add-dialog')) {
  customElements.define('sd-quick-add-dialog', SDQuickAddDialog);
}
